import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import Form, { FormMethod } from "@saleor/components/Form";
import { FormSpacer } from "@saleor/components/FormSpacer";
import { DEMO_MODE } from "@saleor/config";
import { commonMessages } from "@saleor/intl";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { userSignupPath } from "./../../urls";
export interface FormData {
  email: string;
  password: string;
}

const useStyles = makeStyles(
  theme => ({
    buttonContainer: {
      display: "flex",
      justifyContent: "flex-end"
    },
    interestedSeller: {
      "& span": {
        borderBottom: "1px solid"
      },
      color: theme.palette.primary.main,
      display: "inline-block",
      fontSize: "13px",
      margin: "15px 0 0",
      textDecoration: "none"
    },
    link: {
      color: theme.palette.primary.main,
      cursor: "pointer"
    },
    loginButton: {
      margin: "-10px 0 0",
      width: 140
    },
    panel: {
      "& .MuiTypography-root": {
        background: "#B20B0B",
        borderRadius: 0,
        color: "#FFFFFF",
        fontSize: ".85rem",
        padding: "10px 15px"
      },
      "& span": {
        color: theme.palette.error.contrastText
      },
      borderRadius: 0,
      left: 0,
      marginTop: "-43px",
      position: "absolute",
      right: 0,
      top: 0
    }
  }),
  { name: "LoginCard" }
);

export interface LoginCardProps {
  canRegister: boolean;
  canResetPassword: boolean;
  error: boolean;
  disableLoginButton: boolean;
  onPasswordRecovery: () => void;
  onSubmit?(event: FormData);
}

const LoginCard: React.FC<LoginCardProps> = props => {
  const {
    error,
    disableLoginButton,
    onPasswordRecovery,
    onSubmit,
    canResetPassword,
    canRegister
  } = props;
  const [showPassword, setShowPassword] = useState(false);

  const classes = useStyles(props);
  const intl = useIntl();

  let initialFormData = { email: "", password: "" };
  if (DEMO_MODE) {
    initialFormData = {
      email: "",
      password: ""
    };
  }

  const handleClickShowPassword = () => {
    setShowPassword(prevShowPassword => !prevShowPassword);
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  return (
    <Form
      initial={initialFormData}
      onSubmit={onSubmit}
      method={FormMethod.POST}
    >
      {({ change: handleChange, data, submit: handleSubmit }) => (
        <>
          {error && (
            <div className={classes.panel} data-test="loginErrorMessage">
              <Typography variant="caption">
                <FormattedMessage defaultMessage="Sorry, your username and/or password are incorrect. Please try again." />
              </Typography>
            </div>
          )}
          <TextField
            autoFocus
            fullWidth
            autoComplete="username"
            label={intl.formatMessage(commonMessages.email)}
            name="email"
            onChange={handleChange}
            value={data.email}
            inputProps={{
              "data-test": "email"
            }}
          />
          <FormSpacer />
          <TextField
            fullWidth
            id="standard-adornment-password"
            autoComplete="password"
            label={intl.formatMessage({
              defaultMessage: "Password"
            })}
            name="password"
            onChange={handleChange}
            type={showPassword ? "text" : "password"}
            value={data.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <FormSpacer />
          {canResetPassword ? (
            <Typography className={classes.link} onClick={onPasswordRecovery}>
              <FormattedMessage
                defaultMessage="Forgot your password?"
                description="button"
              />
            </Typography>
          ) : null}
          <FormSpacer />
          <div className={classes.buttonContainer}>
            <Button
              className={classes.loginButton}
              color="primary"
              disabled={disableLoginButton}
              variant="contained"
              onClick={handleSubmit}
              type="submit"
              data-test="submit"
            >
              <FormattedMessage defaultMessage="Login" description="button" />
            </Button>
          </div>
          {canRegister ? (
            <Link to={userSignupPath} className={classes.interestedSeller}>
              Interested in being an Online Seller? <span>Sign up here!</span>
            </Link>
          ) : null}
        </>
      )}
    </Form>
  );
};
LoginCard.displayName = "LoginCard";
export default LoginCard;
